import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemMediaFrame,
  ItemMetaField,
  ItemMetaFieldText,
  ItemMetaRow,
  ItemSnippet,
  ItemTitle,
  ItemTitleText,
  ItemTrigger,
} from 'designSystem/components/Item'
import {
  type DocumentTableRow,
  formatDocumentDisplayTitle,
} from 'features/knowledgeDocumentViewer'
import { DocumentThumbnail } from 'features/knowledgeDocumentViewer/'
import { DownloadSearchResultButton } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsSearchActions/DownloadSearchResultButton'
import { FileDigit, Store } from 'lucide-react'
import React from 'react'

const SUMMARY_MAX_LENGTH = 240

interface DocumentResultCardProps {
  document: DocumentTableRow
  isCompact?: boolean
  onClick: () => void
  onDoubleClick: () => void
  cardRef: (el: HTMLButtonElement | null) => void
}

function truncateSummary(summary: string): string {
  if (summary.length <= SUMMARY_MAX_LENGTH) return summary
  return summary.slice(0, SUMMARY_MAX_LENGTH).trimEnd() + '…'
}

export const DocumentResultCard: React.FC<DocumentResultCardProps> = ({
  document,
  isCompact = false,
  onClick,
  onDoubleClick,
  cardRef,
}) => {
  const rawDisplayTitle = document.documentTitle || document.title
  const displayTitle = formatDocumentDisplayTitle(
    document.documentTitle,
    document.title
  )
  const displayDrawingNumber =
    document.drawing ||
    (rawDisplayTitle !== document.title ? document.title : null)

  const summarySnippet = document.summary
    ? truncateSummary(document.summary)
    : null

  const hasMeta = Boolean(document.vendor) || Boolean(displayDrawingNumber)
  const cardSize = isCompact ? 'compact' : 'lg'

  return (
    <Item size={cardSize} variant='surface'>
      <ItemTrigger
        ref={cardRef}
        type='button'
        onClick={onClick}
        onDoubleClick={onDoubleClick}>
        <ItemMedia variant='thumbnail'>
          <ItemMediaFrame>
            <DocumentThumbnail
              documentId={document.id}
              alt={displayTitle}
              filename={document.filename}
              bqDocumentId={document.bqDocumentId}
              cantileverDocumentId={document.cantileverDocumentId}
              documentHostsHydrated
              maxDimensionRem={isCompact ? 4 : 6}
            />
          </ItemMediaFrame>
        </ItemMedia>
        <ItemContent variant='document'>
          <ItemTitle variant='document'>
            <ItemTitleText>{displayTitle}</ItemTitleText>
          </ItemTitle>
          {summarySnippet && <ItemSnippet>{summarySnippet}</ItemSnippet>}
          {hasMeta && (
            <ItemMetaRow gap='relaxed'>
              {document.vendor && (
                <ItemMetaField>
                  <Store strokeWidth={1.75} />
                  <ItemMetaFieldText>{document.vendor}</ItemMetaFieldText>
                </ItemMetaField>
              )}
              {displayDrawingNumber && (
                <ItemMetaField>
                  <FileDigit strokeWidth={1.75} />
                  <ItemMetaFieldText>{displayDrawingNumber}</ItemMetaFieldText>
                </ItemMetaField>
              )}
            </ItemMetaRow>
          )}
        </ItemContent>
      </ItemTrigger>
      <ItemActions variant='trailing'>
        <DownloadSearchResultButton
          document={document}
          size={isCompact ? 'icon' : 'icon-lg'}
        />
      </ItemActions>
    </Item>
  )
}
